<template>
    <aside class="sidebar">
        <div class="logo-container">
            <img :src="logoUrl" class="logo" alt="系统logo" />
        </div>

        <div class="patient-selector">
            <div
                class="selector-header"
                @click="togglePatientList"
                :class="{ 'active-patient': isPatientRoute }"
            >
                <span>{{ selectedPatient || "病人选择" }}</span>
                <i
                    :class="[
                        'fas',
                        isPatientListOpen ? 'fa-chevron-up' : 'fa-chevron-down',
                    ]"
                ></i>
            </div>

            <transition name="slide">
                <div v-show="isPatientListOpen" class="patient-list">
                    <div class="search-box">
                        <input
                            v-model="searchQuery"
                            placeholder="搜索患者..."
                            class="search-input"
                        />
                        <i class="fas fa-search search-icon"></i>
                    </div>
                    <div class="patient-items">
                        <div v-if="patients.length === 0" class="empty-tip">
                            ⚠️ 暂无患者数据
                        </div>
                        <div
                            v-else
                            v-for="patient in filteredPatients"
                            :key="patient.id"
                            class="patient-item"
                            @click="selectPatient(patient)"
                        >
                            {{ patient.name }}
                            <span class="patient-age">{{ patient.age }}岁</span>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <nav class="menu">
            <router-link
                v-for="item in menuItems"
                :key="item.path"
                :to="item.path"
                class="menu-item"
                :class="{ active: $route.name === item.name }"
                exact
                @click="(event) => handleMenuItemClick(event, item)"
            >
                <i :class="item.icon"></i>
                <span class="menu-text">{{ item.title }}</span>
            </router-link>
        </nav>

        <div class="settings">
            <button @click="logout" class="menu-item">
                <i class="fas fa-sign-out-alt"></i>
                <span class="menu-text">退出登录</span>
            </button>
        </div>
    </aside>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/store";

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

const activePage = defineProps({
    activePage: String,
});

const logoUrl = ref(require("@/assets/images/Xinjian-logo.png"));
const isPatientListOpen = ref(false);
const searchQuery = ref("");
const selectedPatient = ref(null);
const patients = ref(userStore.patients);
const menuItems = ref([
    {
        path: "/eeg-record",
        name: "EEGRecord",
        title: "睡眠信号监测",
        icon: "fas fa-brain",
        requirePatient: false
    },
    {
        path: "/sleep-report",
        name: "SleepReport",
        title: "睡眠监测报告",
        icon: "fas fa-moon",
        requirePatient: true
    },
    {
        path: "/depression-report",
        name: "DepressionReport",
        title: "抑郁症诊断报告",
        icon: "fas fa-heartbeat",
        requirePatient: true
    },
    {
        path: "/questionnaire-management",
        name: "QuestionnaireManagement",
        title: "问卷管理",
        icon: "fas fa-clipboard-list",
        requirePatient: true
    },
    // 暂时隐藏睡眠改善方案菜单项
    // {
    //     path: "/treatment-plan-management",
    //     name: "TreatmentPlanManagement",
    //     title: "睡眠改善方案",
    //     icon: "fas fa-stethoscope",
    //     requirePatient: true
    // },
    {
        path: "/light-therapy",
        name: "LightTherapy",
        title: "脑机智能疗愈灯",
        icon: "fas fa-lightbulb",
        requirePatient: true
    },
    {
        path: "/ai",
        name: "AI",
        title: "脑电智能助手",
        icon: "fas fa-comments",
        requirePatient: false
    },
    {
        path: "/reference",
        name: "Reference",
        title: "指标参考",
        icon: "fas fa-chart-line",
        requirePatient: false
    },
    {
        path: "/account-settings",
        name: "AccountSettings",
        title: "账户设置",
        icon: "fas fa-user-cog",
        requirePatient: false
    },
]);
const isPatientSelected = ref(false);


const filteredPatients = computed(() => {
    return patients.value.filter((patient) =>
        patient.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
});

const isPatientRoute = computed(() => {
    return route.path.startsWith("/patient/");
});

// 处理菜单项点击事件的通用函数
const handleMenuItemClick = (event, item) => {
  // 只对需要患者的页面进行检查
  if (item.requirePatient) {
    if (!selectedPatient.value || selectedPatient.value.trim() === '') {
      // 阻止导航
      event.preventDefault();
      event.stopPropagation();
      // 显示提示信息
      alert('请先选择一位患者');
    }
  }
}

const togglePatientList = () => {
    isPatientListOpen.value = !isPatientListOpen.value;
};

const selectPatient = async (patient) => {
    selectedPatient.value = patient.name;
    isPatientListOpen.value = false;
    await userStore.fetchPatientDetail(patient.id);
    router.push(`/patient/${patient.id}`);
    isPatientSelected.value = true;
};

const logout = async () => {
    try {
        await userStore.logout();
        this.$router.replace({ name: "Login" });
    } catch (error) {
        // console.error('退出登录失败:', error)
    }
};

watch(
    () => route.params.id,
    (newId) => {
        isPatientSelected.value = !!newId;
        if (newId) {
            const patient = patients.value.find((p) => p.id == newId);
            selectedPatient.value = patient?.name || null;
        }
    }
);

onMounted(() => {
    const newId = route.params.id;
    isPatientSelected.value = !!newId;
    if (newId) {
        const patient = patients.value.find((p) => p.id == newId);
        selectedPatient.value = patient?.name || null;
    }
});
</script>
<style scoped>
.sidebar {
    position: fixed;
    left: 0;
    top: 0;
    width: min(200px, 27.8vw);
    height: 100vh;
    background: #fcfcff;
    box-shadow: 4px 0 15px rgba(113, 53, 230, 0.03);
    display: flex;
    flex-direction: column;
    z-index: 1000;
    border-right: 1px solid #f0f0ff;
    overflow: hidden;
    font-size: min(14px, 1.9vw);
}

.logo-container {
    padding: 5%;
    border-bottom: 1px solid #eee;
}

.logo {
    width: 100%;
    max-width: min(180px, 25vw);
    display: block;
    margin: 0 auto;
}

.patient-selector {
    padding: 12px;
    border-bottom: 1px solid #f5f5ff;
}

.selector-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: min(12px, 1.7vh) min(20px, 2.8vw);
    background: rgba(113, 53, 230, 0.03);
    border-radius: min(8px, 1.1vw);
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: min(16px, 2.2vw);
}

.selector-header:hover {
    background: rgba(113, 53, 230, 0.08);
    color: var(--primary-color);
}

.selector-header i {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.selector-header.active-patient {
    background: rgba(113, 53, 230, 0.1) !important;
    border: 1px solid rgba(113, 53, 230, 0.2);
    color: var(--primary-color);
    position: relative;
}

/* .selector-header.active-patient::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--primary-color);
  animation: borderGrow 0.3s ease-out;
} */

@keyframes borderGrow {
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
}

.selector-header.active-patient i {
    color: var(--primary-color);
}

.patient-list {
    margin-top: 10px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(113, 53, 230, 0.05);
    overflow: hidden;
    border: 1px solid #f5f5ff;
    max-height: 300px;
    transition: max-height 0.3s ease-in-out;
    will-change: transform, opacity;
}

.search-box {
    position: relative;
    padding: 10px;
}

.search-input {
    width: 100%;
    padding: 8px 30px 8px 15px;
    border: 1px solid #ddd;
    border-radius: 20px;
    font-size: 14px;
}

.search-icon {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
}

.patient-items {
    max-height: 200px;
    overflow-y: auto;
}

.patient-item {
    padding: 10px 16px;
    cursor: pointer;
    transition: all 0.2s ease-out;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.patient-item:hover {
    background: rgba(113, 53, 230, 0.03);
}

.patient-age {
    font-size: 12px;
    color: #999;
}

.slide-enter-active {
    transition: opacity 0.2s ease-out,
        transform 0.25s cubic-bezier(0.18, 0.89, 0.32, 1.28),
        max-height 0.3s ease-in-out;
    transform-origin: top;
}

.slide-leave-active {
    transition: opacity 0.15s ease-in,
        transform 0.2s cubic-bezier(0.55, 0.06, 0.68, 0.19),
        max-height 0.25s ease-in-out;
}

.slide-enter-from {
    opacity: 0;
    transform: translateY(-8px);
    max-height: 0;
}

.slide-leave-to {
    opacity: 0;
    transform: translateY(-4px);
    max-height: 0 !important;
}

.menu {
    flex: 1;
    padding: 20px 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.menu-item {
    display: flex;
    align-items: center;
    justify-content: start;
    padding: min(14px, 1.9vh) 0;
    padding-left: min(10px, 1.4vw);
    margin: min(8px, 1.1vh) min(20px, 2.8vw);
    color: #666;
    border-radius: min(8px, 1.1vw);
    transition: transform 0.2s ease-out,
        background 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    text-decoration: none;
    position: relative;
    overflow: hidden;
}

.menu-item:hover {
    transform: translateX(4px);
    background: rgba(113, 53, 230, 0.03);
}

.menu-item.active {
    background: linear-gradient(
        135deg,
        var(--primary-color) 0%,
        var(--primary-color-lighter) 100%
    );
    box-shadow: 0 2px 6px rgba(113, 53, 230, 0.15);
    color: white;
    animation: buttonActive 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-item.active::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.1) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.1) 75%,
        transparent 75%,
        transparent
    );
    background-size: 40px 40px;
    animation: activeShine 2s linear infinite;
    opacity: 0.3;
}

@keyframes buttonActive {
    0% {
        transform: scale(0.95);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes activeShine {
    from {
        background-position: 0 0;
    }
    to {
        background-position: 40px 40px;
    }
}

.menu-item.active .menu-text {
    font-weight: 400;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.menu-item.active i {
    color: rgba(255, 255, 255, 0.9) !important;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
    animation: iconPulse 1.5s ease-in-out infinite;
}

.menu-item i {
    width: 24px;
    margin-right: 12px;
    font-size: 18px;
    transition: transform 0.2s ease-out,
        color 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-item:hover i {
    transform: scale(1.1);
    color: var(--primary-color);
}

.menu-text {
    font-size: 15px;
    font-family: "SourceHanSansCN-Regular", sans-serif;
}

.settings {
    border-top: 1px solid #eee;
    padding: 20px 0;
    background: #fcfcff;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .sidebar {
        width: min(60px, 8.3vw);
        box-shadow: 4px 0 12px rgba(113, 53, 230, 0.05);
    }

    .logo {
        max-width: min(50px, 6.9vw);
    }

    .menu-text {
        display: none;
    }

    .menu-item {
        justify-content: center;
        padding: min(14px, 1.9vh) 0;
        margin: min(8px, 1.1vh) min(5px, 0.7vw);
    }

    .menu-item i {
        margin-right: 0;
        font-size: min(18px, 2.5vw);
    }

    .menu-item:hover {
        transform: translateY(-2px);
    }

    .menu-item.active {
        box-shadow: 0 2px 4px rgba(113, 53, 230, 0.2) inset;
        animation: none;
    }

    .menu-item.active::after {
        display: none;
    }

    .menu {
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .menu::-webkit-scrollbar {
        display: none;
    }
}

/* 轻盈滚动条 */
.patient-items::-webkit-scrollbar {
    width: 4px;
}

.patient-items::-webkit-scrollbar-thumb {
    background: rgba(113, 53, 230, 0.15);
    border-radius: 2px;
}

.empty-tip {
    text-align: center;
    padding-bottom: 10px;
}
</style>